/**
 * @fileoverview Tool configuration mappings for the ToolCallMessage component.
 * Provides a data-driven approach to rendering tool calls instead of
 * repetitive conditional blocks.
 */

/**
 * Configuration for how a tool should be displayed.
 */
export interface ToolConfig {
  /** The display text for the tool (e.g., "Executing code", "Creating task:") */
  text: string;
  /** Optional function to extract detail text from input */
  getDetail?: (input: Record<string, unknown>) => string | undefined;
  /** Optional function to extract detail text from output when completed */
  getOutputDetail?: (output: Record<string, unknown>, input?: Record<string, unknown>) => string | undefined;
  /** Whether this tool shows success/failure status badge (text like "Rewritten", "Deleted") */
  showStatus?: boolean;
  /** Whether to show checkmark icon when completed (without text badge) */
  showCheckmark?: boolean;
}

/**
 * Configuration object mapping tool names to their display settings.
 * This replaces 50+ conditional blocks with a single lookup.
 */
export const TOOL_CONFIGS: Record<string, ToolConfig> = {
  // Data & Analytics Tools
  data_catalog_search: {
    text: 'Searching data for:',
    getDetail: (input) => input.description,
    getOutputDetail: (output, input) => output.message || `Searched data for: ${input?.description || 'query'}`,
    showCheckmark: true,
  },
  generate_visualization: {
    text: 'Generating visualization',
    getOutputDetail: (output) => output.message || 'Generated visualization',
    showCheckmark: true,
  },
  execute_code: {
    text: 'Executing code',
    getOutputDetail: (output) => output.message || 'Executed code',
    showCheckmark: true,
  },

  // Skills
  load_skill: {
    text: 'Loading skill:',
    getDetail: (input) => {
      const slug = input?.slug;
      if (typeof slug !== 'string' || !slug) return undefined;
      // Slug arrives kebab-case during streaming (the resolved `name` only
      // exists in the output). Title-case it so the user sees a human label
      // before the tool resolves.
      return slug
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    getOutputDetail: (output, input) => {
      if (output?.ok && typeof output?.name === 'string') {
        return `Loaded skill: ${output.name}`;
      }
      if (typeof output?.error === 'string') return output.error;
      const slug = input?.slug;
      if (typeof slug === 'string' && slug) {
        const label = slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        return `Loaded skill: ${label}`;
      }
      return 'Skill loaded';
    },
    showCheckmark: true,
  },

  // Agent & Communication Tools
  make_agent_call: {
    text: 'Calling',
    getDetail: (input) => input.agentName,
    getOutputDetail: (output, input) => output.message || `Called ${input?.agentName || 'agent'}`,
    showCheckmark: true,
  },
  spawn_worker_agents: {
    text: 'Spawning temporary workers',
    getDetail: (input) => {
      const workers = input?.workers;
      if (!Array.isArray(workers)) return undefined;
      return `${workers.length} worker${workers.length === 1 ? '' : 's'}`;
    },
    getOutputDetail: (output) => {
      const count = output?.workerCount;
      const failed = output?.failedCount;
      if (typeof count === 'number') {
        return failed ? `Completed ${count - failed}/${count} workers` : `Completed ${count} workers`;
      }
      return 'Temporary workers completed';
    },
    showCheckmark: true,
  },
  sendEmail: {
    text: 'Sending email to',
    getDetail: (input) => input.to,
    getOutputDetail: (output, input) => output.message || `Sent email to ${input?.to || 'recipient'}`,
    showCheckmark: true,
  },
  send_email_tool: {
    text: 'Sending email to',
    getDetail: (input) => input.to,
    getOutputDetail: (output, input) => output.message || `Sent email to ${input?.to || 'recipient'}`,
    showCheckmark: true,
  },
  call_subordinates: {
    text: 'Calling subordinate agent',
    getOutputDetail: (output) => output.message || 'Called subordinate agent',
    showCheckmark: true,
  },

  // Search Tools
  searchInternet: {
    text: 'Searching internet:',
    getDetail: (input) => input.query,
    getOutputDetail: (output, input) => output.message || `Searched internet: ${input?.query || 'query'}`,
    showCheckmark: true,
  },
  search_web: {
    text: 'Searching web:',
    getDetail: (input) => input.query || 'Web search',
    getOutputDetail: (output, input) => output.message || `Searched web: ${input?.query || 'query'}`,
    showCheckmark: true,
  },
  search_duckduckgo: {
    text: 'DuckDuckGo search:',
    getDetail: (input) => input.query || 'Search query',
    getOutputDetail: (output, input) => output.message || `Searched: ${input?.query || 'query'}`,
    showCheckmark: true,
  },
  search_knowledge: {
    text: 'Searching knowledge:',
    getDetail: (input) => input.query || input.question || 'Knowledge search',
    getOutputDetail: (output, input) => output.message || `Searched knowledge: ${input?.query || input?.question || 'query'}`,
    showCheckmark: true,
  },
  enhanced_search: {
    text: 'Searching knowledge:',
    getDetail: (input) => input.query || input.question || 'Knowledge search',
    getOutputDetail: (output, input) => output.message || `Searched knowledge: ${input?.query || input?.question || 'query'}`,
    showCheckmark: true,
  },
  deep_research: {
    text: 'Deep research:',
    getDetail: (input) => input.query || input.topic || 'Research query',
    getOutputDetail: (output, input) => output.message || `Researched: ${input?.query || input?.topic || 'topic'}`,
    showCheckmark: true,
  },
  web_browse: {
    text: 'Browsing web:',
    getDetail: (input) => input.url || input.action || 'Browser action',
    getOutputDetail: (output, input) => {
      if (output?.screenshot?.imageUrl) {
        return `Captured screenshot: ${output.title || output.url || 'browser page'}`;
      }
      if (output?.snapshot?.title || output?.snapshot?.url) {
        return output.snapshot.title || output.snapshot.url;
      }
      if (output?.page?.title || output?.page?.url || output?.page?.finalUrl) {
        return output.page.title || output.page.url || output.page.finalUrl;
      }
      return `Browser action completed: ${input?.action || 'browse'}`;
    },
    showCheckmark: true,
  },

  // Task Management Tools
  create_task: {
    text: 'Creating task:',
    getDetail: (input) => input.name || 'New task',
    getOutputDetail: (output, input) => output.message || `Created task: ${input?.name || 'task'}`,
    showCheckmark: true,
  },
  list_tasks: {
    text: 'Listing tasks',
    getOutputDetail: (output) => output.message || 'Listed tasks',
    showCheckmark: true,
  },
  get_task_status: {
    text: 'Getting task status:',
    getDetail: (input) => input.taskId || 'Task ID',
    getOutputDetail: (output) => output.message || 'Retrieved task status',
    showCheckmark: true,
  },
  execute_task_now: {
    text: 'Executing task now:',
    getDetail: (input) => input.taskId || 'Task ID',
    getOutputDetail: (output) => output.message || 'Executed task',
    showCheckmark: true,
  },
  pause_task: {
    text: 'Pausing task:',
    getDetail: (input) => input.taskId || 'Task ID',
    getOutputDetail: (output) => output.message || 'Paused task',
    showCheckmark: true,
  },
  resume_task: {
    text: 'Resuming task:',
    getDetail: (input) => input.taskId || 'Task ID',
    getOutputDetail: (output) => output.message || 'Resumed task',
    showCheckmark: true,
  },
  delete_task: {
    text: 'Deleting task:',
    getDetail: (input) => input.taskId || 'Task ID',
    getOutputDetail: (output) => output.message || 'Deleted task',
    showCheckmark: true,
  },
  trigger_event: {
    text: 'Triggering event:',
    getDetail: (input) => input.eventType || 'Event',
    getOutputDetail: (output, input) => output.message || `Triggered event: ${input?.eventType || 'event'}`,
    showCheckmark: true,
  },

  // File Tools
  file_management_tool: {
    text: '',
    getDetail: (input) => `Managing files: ${input.operation || 'File operation'}`,
    getOutputDetail: (output, input) => output.message || `Managed files: ${input?.operation || 'operation'}`,
    showCheckmark: true,
  },
  create_file: {
    text: '',
    getDetail: (input) => {
      const path = input.file_path || input.filePath;
      return path ? `Creating file: ${path}` : 'Creating file';
    },
    getOutputDetail: (output, input) => {
      if (typeof output === 'string') return output;
      if (output?.message) return output.message;
      const path = input?.file_path || input?.filePath;
      return path ? `Created file: ${path}` : 'File created';
    },
    showCheckmark: true,
  },
  sandbox_create_file: {
    text: '',
    getDetail: (input) => {
      const path = input.file_path || input.filePath;
      return path ? `Creating file: ${path}` : 'Creating file';
    },
    getOutputDetail: (output, input) => {
      if (typeof output === 'string') return output;
      if (output?.message) return output.message;
      const path = input?.file_path || input?.filePath;
      return path ? `Created file: ${path}` : 'File created';
    },
    showCheckmark: true,
  },
  read_file: {
    text: '',
    getDetail: (input) => {
      const path = input.file_path || input.filePath || 'File';
      if (input.start_line && input.end_line) {
        return `Reading file: ${path} (lines ${input.start_line}-${input.end_line})`;
      }
      return `Reading file: ${path}`;
    },
    getOutputDetail: (output, input) => output.message || `Read file: ${input?.file_path || input?.filePath || 'file'}`,
    showCheckmark: true,
  },
  sandbox_read_file: {
    text: '',
    getDetail: (input) => {
      const path = input.file_path || input.filePath || 'File';
      if (input.start_line && input.end_line) {
        return `Reading file: ${path} (lines ${input.start_line}-${input.end_line})`;
      }
      return `Reading file: ${path}`;
    },
    getOutputDetail: (output, input) => output.message || `Read file: ${input?.file_path || input?.filePath || 'file'}`,
    showCheckmark: true,
  },
  str_replace: {
    text: '',
    getDetail: (input) => `Editing file: ${input.file_path || input.filePath || 'File'}`,
    getOutputDetail: (output, input) => output.message || `Edited file: ${input?.file_path || input?.filePath || 'file'}`,
    showCheckmark: true,
  },
  sandbox_str_replace: {
    text: '',
    getDetail: (input) => `Editing file: ${input.file_path || input.filePath || 'File'}`,
    getOutputDetail: (output, input) => output.message || `Edited file: ${input?.file_path || input?.filePath || 'file'}`,
    showCheckmark: true,
  },

  // Local File System Tools — client-executed via File System Access API.
  // The agent receives `path`, `content`, etc. directly (no file_path alias).
  local_fs_read_file: {
    text: '',
    getDetail: (input) => {
      const path = input.path || 'file';
      if (input.start_line && input.end_line) {
        return `Reading: ${path} (lines ${input.start_line}-${input.end_line})`;
      }
      return `Reading: ${path}`;
    },
    getOutputDetail: (output, input) => output?.message || `Read: ${input?.path ?? 'file'}`,
    showCheckmark: true,
  },
  local_fs_write_file: {
    text: '',
    getDetail: (input) => `Editing: ${input.path || 'file'}`,
    getOutputDetail: (output, input) => output?.message || `Edited: ${input?.path ?? 'file'}`,
    showCheckmark: true,
  },
  local_fs_create_file: {
    text: '',
    getDetail: (input) => `Creating: ${input.path || 'file'}`,
    getOutputDetail: (output, input) => output?.message || `Created: ${input?.path ?? 'file'}`,
    showCheckmark: true,
  },
  local_fs_delete_file: {
    text: '',
    getDetail: (input) => `Deleting: ${input.path || 'file'}`,
    getOutputDetail: (output, input) => output?.message || `Deleted: ${input?.path ?? 'file'}`,
    showCheckmark: true,
  },
  local_fs_list_directory: {
    text: '',
    getDetail: (input) => `Listing: ${input.path || '.'}`,
    getOutputDetail: (output, input) => output?.message || `Listed: ${input?.path ?? '.'}`,
    showCheckmark: true,
  },
  local_fs_search_in_files: {
    text: '',
    getDetail: (input) => {
      const where = input.path ? ` in ${input.path}` : '';
      return `Searching for "${input.pattern || ''}"${where}`;
    },
    getOutputDetail: (output, input) =>
      output?.message ||
      `Searched for "${input?.pattern ?? ''}"${input?.path ? ` in ${input.path}` : ''}`,
    showCheckmark: true,
  },
  sandbox_write_file: {
    text: '',
    getDetail: (input) => `Writing file: ${input.file_path || input.filePath || input.path || 'File'}`,
    getOutputDetail: (output, input) => output.message || `Wrote file: ${input?.file_path || input?.filePath || input?.path || 'file'}`,
    showCheckmark: true,
  },
  full_file_rewrite: {
    text: '',
    getDetail: (input) => `Rewriting file: ${input.file_path || input.filePath || 'File'}`,
    getOutputDetail: (output, input) => output.message || `Rewrote file: ${input?.file_path || input?.filePath || 'file'}`,
    showStatus: true,
  },
  sandbox_full_file_rewrite: {
    text: '',
    getDetail: (input) => `Rewriting file: ${input.file_path || input.filePath || 'File'}`,
    getOutputDetail: (output, input) => output.message || `Rewrote file: ${input?.file_path || input?.filePath || 'file'}`,
    showStatus: true,
  },
  delete_file: {
    text: '',
    getDetail: (input) => `Deleting file: ${input.file_path || input.filePath || 'File'}`,
    getOutputDetail: (output, input) => output.message || `Deleted file: ${input?.file_path || input?.filePath || 'file'}`,
    showStatus: true,
  },
  sandbox_delete_file: {
    text: '',
    getDetail: (input) => `Deleting file: ${input.file_path || input.filePath || 'File'}`,
    getOutputDetail: (output, input) => output.message || `Deleted file: ${input?.file_path || input?.filePath || 'file'}`,
    showStatus: true,
  },

  // Web Scraping Tools
  website_scraping: {
    text: 'Scraping website:',
    getDetail: (input) => input.url || 'No URL provided',
    getOutputDetail: (output, input) => output.message || `Scraped website: ${input?.url || 'URL'}`,
    showCheckmark: true,
  },
  scrape_webpage: {
    text: 'Scraping webpage:',
    getDetail: (input) => {
      const urls = input.urls || input.url || '';
      const urlList = urls.split(',').map((u: string) => u.trim()).filter(Boolean);
      if (urlList.length === 1) return urlList[0];
      if (urlList.length > 1) return `${urlList.length} URLs`;
      return 'Web scraping';
    },
    getOutputDetail: (output) => output.message || 'Scraped webpage',
    showCheckmark: true,
  },
  content_parser_tool: {
    text: 'Parsing HTML content',
    getOutputDetail: (output) => output.message || 'Parsed HTML content',
    showCheckmark: true,
  },
  content_extraction: {
    text: 'Extracting content insights',
    getOutputDetail: (output) => output.message || 'Extracted content insights',
    showCheckmark: true,
  },

  // SEO & Marketing Tools
  apollo_company_search: {
    text: 'Searching companies:',
    getDetail: (input) => input.industry || input.location || 'Company search',
    getOutputDetail: (output) => output.message || 'Searched companies',
    showCheckmark: true,
  },
  google_keyword_planner: {
    text: 'Planning keywords:',
    getDetail: (input) => input.keyword || 'Keyword research',
    getOutputDetail: (output) => output.message || 'Planned keywords',
    showCheckmark: true,
  },
  dataforseo_api: {
    text: 'Analyzing SEO data',
    getOutputDetail: (output) => output.message || 'Analyzed SEO data',
    showCheckmark: true,
  },
  content_gap_analysis: {
    text: 'Analyzing content gaps',
    getOutputDetail: (output) => output.message || 'Analyzed content gaps',
    showCheckmark: true,
  },
  claude_content_generator: {
    text: 'Generating content with AI:',
    getDetail: (input) => input.topic || input.title || 'Content generation',
    getOutputDetail: (output) => output.message || 'Generated content',
    showCheckmark: true,
  },
  language_tool_grammar_checker: {
    text: 'Checking grammar',
    getOutputDetail: (output) => output.message || 'Checked grammar',
    showCheckmark: true,
  },
  duplichecker_plagiarism: {
    text: 'Checking plagiarism',
    getOutputDetail: (output) => output.message || 'Checked plagiarism',
    showCheckmark: true,
  },
  serpstat_seo_analyzer: {
    text: 'Analyzing SEO metrics',
    getOutputDetail: (output) => output.message || 'Analyzed SEO metrics',
    showCheckmark: true,
  },

  // Sandbox Command Tools
  sandbox_execute_command: {
    text: 'Executing command:',
    getDetail: (input) => input.command || 'Shell command',
    getOutputDetail: (output, input) => output.message || `Executed: ${input?.command || 'command'}`,
    showCheckmark: true,
  },
  sandbox_check_command_output: {
    text: 'Checking command output',
    getOutputDetail: (output) => output.message || 'Checked command output',
    showCheckmark: true,
  },
  sandbox_terminate_command: {
    text: 'Terminating command',
    getOutputDetail: (output) => output.message || 'Terminated command',
    showCheckmark: true,
  },
  sandbox_list_commands: {
    text: 'Listing commands',
    getOutputDetail: (output) => output.message || 'Listed commands',
    showCheckmark: true,
  },
  sandbox_cleanup: {
    text: 'Cleaning up sandbox',
    getOutputDetail: (output) => output.message || 'Cleaned up sandbox',
    showCheckmark: true,
  },

  // Image & PDF Tools
  generate_image: {
    text: 'Generating image:',
    getDetail: (input) => input.prompt || input.description || 'AI image generation',
    getOutputDetail: (output) => output.message || 'Generated image',
    showCheckmark: true,
  },
  image_generation: {
    text: 'Generating image:',
    getDetail: (input) => input.prompt || input.description || 'AI image generation',
    getOutputDetail: (output) => output.message || 'Generated image',
    showCheckmark: true,
  },
  pdf_generator: {
    text: 'Generating PDF:',
    getDetail: (input) => input.filename,
    getOutputDetail: (output, input) => output.message || `Generated PDF: ${input?.filename || 'document'}`,
    showCheckmark: true,
  },
  sandbox_generate_pdf: {
    text: '',
    getDetail: (input) => `Generating PDF: ${input.filename || input.title || 'document'}`,
    getOutputDetail: (output, input) => output.description || `Generated PDF: ${input?.filename || input?.title || 'document'}`,
    showCheckmark: true,
  },
  generate_chart: {
    text: 'Generating chart:',
    getDetail: (input) => input.title || input.description,
    getOutputDetail: (output, input) => output.message || `Generated chart: ${input?.title || 'chart'}`,
    showCheckmark: true,
  },

  // News & Weather Tools
  get_news: {
    text: 'Getting latest news',
    getOutputDetail: (output) => output.message || 'Retrieved latest news',
    showCheckmark: true,
  },
  get_weather: {
    text: 'Getting weather:',
    getDetail: (input) => input.location || 'Weather forecast',
    getOutputDetail: (output, input) => output.message || `Retrieved weather: ${input?.location || 'location'}`,
    showCheckmark: true,
  },

  // Odoo ERP Tools
  odoo_list_models: {
    text: 'Odoo:',
    getDetail: (input) => input.filter
      ? `Listing models: "${input.filter}"`
      : 'Discovering available models',
    getOutputDetail: (output) => output.message || 'Listed Odoo models',
    showCheckmark: true,
  },
  odoo_describe_model: {
    text: 'Odoo:',
    getDetail: (input) => {
      const modelMap: Record<string, string> = {
        'product.product': 'Analyzing products',
        'res.partner': 'Analyzing contacts',
        'sale.order': 'Analyzing sales orders',
        'crm.lead': 'Analyzing leads',
        'helpdesk.ticket': 'Analyzing tickets',
        'hr.employee': 'Analyzing employees',
      };
      const model = input.model || '';
      return modelMap[model] || `Analyzing ${model.replace('.', ' ') || 'model'}`;
    },
    getOutputDetail: (output, input) => {
      if (output.message) return output.message;
      const modelMap: Record<string, string> = {
        'product.product': 'Analyzed products',
        'res.partner': 'Analyzed contacts',
        'sale.order': 'Analyzed sales orders',
        'crm.lead': 'Analyzed leads',
        'helpdesk.ticket': 'Analyzed tickets',
        'hr.employee': 'Analyzed employees',
      };
      const model = input?.model || '';
      return modelMap[model] || `Analyzed ${model.replace('.', ' ') || 'model'}`;
    },
    showCheckmark: true,
  },
  odoo_search_read: {
    text: 'Odoo:',
    getDetail: (input) => {
      const modelMap: Record<string, string> = {
        'product.product': 'Fetching products',
        'product.template': 'Fetching product templates',
        'res.partner': 'Fetching contacts',
        'res.users': 'Fetching users',
        'sale.order': 'Fetching sales orders',
        'sale.order.line': 'Fetching order lines',
        'purchase.order': 'Fetching purchase orders',
        'account.move': 'Fetching invoices',
        'account.move.line': 'Fetching invoice lines',
        'account.payment': 'Fetching payments',
        'crm.lead': 'Fetching leads',
        'hr.employee': 'Fetching employees',
        'stock.picking': 'Fetching deliveries',
        'stock.move': 'Fetching stock movements',
        'stock.quant': 'Fetching inventory',
        'project.project': 'Fetching projects',
        'project.task': 'Fetching tasks',
        'mail.message': 'Fetching messages',
        'res.company': 'Fetching company info',
        'res.currency': 'Fetching currencies',
        'res.country': 'Fetching countries',
      };
      const model = input.model || '';
      return modelMap[model] || `Fetching ${model.replace('.', ' ') || 'records'}`;
    },
    getOutputDetail: (output, input) => {
      if (output.message) return output.message;
      const modelMap: Record<string, string> = {
        'product.product': 'Fetched products',
        'product.template': 'Fetched product templates',
        'res.partner': 'Fetched contacts',
        'res.users': 'Fetched users',
        'sale.order': 'Fetched sales orders',
        'sale.order.line': 'Fetched order lines',
        'purchase.order': 'Fetched purchase orders',
        'account.move': 'Fetched invoices',
        'account.move.line': 'Fetched invoice lines',
        'account.payment': 'Fetched payments',
        'crm.lead': 'Fetched leads',
        'hr.employee': 'Fetched employees',
        'stock.picking': 'Fetched deliveries',
        'stock.move': 'Fetched stock movements',
        'stock.quant': 'Fetched inventory',
        'project.project': 'Fetched projects',
        'project.task': 'Fetched tasks',
        'mail.message': 'Fetched messages',
        'res.company': 'Fetched company info',
        'res.currency': 'Fetched currencies',
        'res.country': 'Fetched countries',
      };
      const model = input?.model || '';
      return modelMap[model] || `Fetched ${model.replace('.', ' ') || 'records'}`;
    },
    showCheckmark: true,
  },
  odoo_create: {
    text: 'Odoo:',
    getDetail: (input) => {
      const modelMap: Record<string, string> = {
        'product.product': 'Creating product',
        'product.template': 'Creating product template',
        'res.partner': 'Creating contact',
        'res.users': 'Creating user',
        'sale.order': 'Creating sales order',
        'sale.order.line': 'Creating order line',
        'purchase.order': 'Creating purchase order',
        'account.move': 'Creating invoice',
        'account.move.line': 'Creating invoice line',
        'account.payment': 'Creating payment',
        'crm.lead': 'Creating lead',
        'hr.employee': 'Creating employee',
        'stock.picking': 'Creating delivery',
        'stock.move': 'Creating stock movement',
        'stock.quant': 'Creating inventory record',
        'project.project': 'Creating project',
        'project.task': 'Creating task',
        'mail.message': 'Creating message',
        'res.company': 'Creating company',
        'res.currency': 'Creating currency',
        'res.country': 'Creating country',
      };
      const model = input.model || '';
      return modelMap[model] || `Creating ${model.replace('.', ' ') || 'record'}`;
    },
    getOutputDetail: (output, input) => {
      if (output.message) return output.message;
      const modelMap: Record<string, string> = {
        'product.product': 'Created product',
        'product.template': 'Created product template',
        'res.partner': 'Created contact',
        'res.users': 'Created user',
        'sale.order': 'Created sales order',
        'sale.order.line': 'Created order line',
        'purchase.order': 'Created purchase order',
        'account.move': 'Created invoice',
        'account.move.line': 'Created invoice line',
        'account.payment': 'Created payment',
        'crm.lead': 'Created lead',
        'hr.employee': 'Created employee',
        'stock.picking': 'Created delivery',
        'stock.move': 'Created stock movement',
        'stock.quant': 'Created inventory record',
        'project.project': 'Created project',
        'project.task': 'Created task',
        'mail.message': 'Created message',
        'res.company': 'Created company',
        'res.currency': 'Created currency',
        'res.country': 'Created country',
      };
      const model = input?.model || '';
      return modelMap[model] || `Created ${model.replace('.', ' ') || 'record'}`;
    },
    showCheckmark: true,
  },
  odoo_write: {
    text: 'Odoo:',
    getDetail: (input) => {
      const modelMap: Record<string, string> = {
        'product.product': 'Updating product',
        'product.template': 'Updating product template',
        'res.partner': 'Updating contact',
        'res.users': 'Updating user',
        'sale.order': 'Updating sales order',
        'sale.order.line': 'Updating order line',
        'purchase.order': 'Updating purchase order',
        'account.move': 'Updating invoice',
        'account.move.line': 'Updating invoice line',
        'account.payment': 'Updating payment',
        'crm.lead': 'Updating lead',
        'hr.employee': 'Updating employee',
        'stock.picking': 'Updating delivery',
        'stock.move': 'Updating stock movement',
        'stock.quant': 'Updating inventory record',
        'project.project': 'Updating project',
        'project.task': 'Updating task',
        'mail.message': 'Updating message',
        'res.company': 'Updating company',
        'res.currency': 'Updating currency',
        'res.country': 'Updating country',
      };
      const model = input.model || '';
      return modelMap[model] || `Updating ${model.replace('.', ' ') || 'record'}`;
    },
    getOutputDetail: (output, input) => {
      if (output.message) return output.message;
      const modelMap: Record<string, string> = {
        'product.product': 'Updated product',
        'product.template': 'Updated product template',
        'res.partner': 'Updated contact',
        'res.users': 'Updated user',
        'sale.order': 'Updated sales order',
        'sale.order.line': 'Updated order line',
        'purchase.order': 'Updated purchase order',
        'account.move': 'Updated invoice',
        'account.move.line': 'Updated invoice line',
        'account.payment': 'Updated payment',
        'crm.lead': 'Updated lead',
        'hr.employee': 'Updated employee',
        'stock.picking': 'Updated delivery',
        'stock.move': 'Updated stock movement',
        'stock.quant': 'Updated inventory record',
        'project.project': 'Updated project',
        'project.task': 'Updated task',
        'mail.message': 'Updated message',
        'res.company': 'Updated company',
        'res.currency': 'Updated currency',
        'res.country': 'Updated country',
      };
      const model = input?.model || '';
      return modelMap[model] || `Updated ${model.replace('.', ' ') || 'record'}`;
    },
    showCheckmark: true,
  },
  odoo_execute: {
    text: 'Odoo:',
    getDetail: (input) => {
      const methodDisplay = input.method
        ? input.method.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
        : 'method';
      const model = input.model || '';
      return `Executing ${methodDisplay}${model ? ` on ${model}` : ''}`;
    },
    getOutputDetail: (output, input) => {
      if (output.message) return output.message;
      const methodDisplay = input?.method
        ? input.method.replace(/_/g, ' ')
        : 'method';
      return `Executed ${methodDisplay}`;
    },
    showCheckmark: true,
  },
};

/**
 * Browser tool text configurations.
 * Uses a switch pattern due to the many variations.
 */
export const getBrowserToolText = (toolName: string, input: Record<string, unknown>): string => {
  switch (toolName) {
    // AI-powered browser tools
    case 'browser_navigate':
      return `Navigating to: ${input.url || 'URL'}`;
    case 'browser_act':
      return `Browser action: ${input.action || input.instruction || 'Performing action'}`;
    case 'browser_extract':
      return `Extracting data: ${input.instruction || 'from page'}`;
    case 'browser_screenshot':
      return 'Taking screenshot';
    // Legacy browser tools
    case 'browser_navigate_to':
      return `Navigating to: ${input.url || 'URL'}`;
    case 'browser_go_back':
      return 'Going back';
    case 'browser_wait':
      return `Waiting for ${input.seconds || 3} seconds`;
    case 'browser_click_element':
      return `Clicking element at index: ${input.index}`;
    case 'browser_list_inputs':
      return 'Listing browser input elements';
    case 'browser_input_text':
      return `Typing "${input.text}" into element at index: ${input.index}`;
    case 'browser_extract_elements':
      return 'Extracting clickable elements';
    case 'browser_scroll_down':
      return `Scrolling down by ${input.amount || 500}px`;
    case 'browser_scroll_up':
      return `Scrolling up by ${input.amount || 500}px`;
    case 'browser_scroll_to_text':
      return `Scrolling to text: "${input.text}"`;
    case 'browser_take_screenshot':
      return 'Taking screenshot';
    case 'browser_send_keys':
      return `Sending keys: ${input.keys}`;
    case 'browser_switch_tab':
      return `Switching to tab: ${input.pageId}`;
    case 'browser_close_tab':
      return `Closing tab: ${input.pageId}`;
    case 'browser_get_dropdown_options':
      return `Getting dropdown options from element at index: ${input.index}`;
    case 'browser_select_dropdown_option':
      return `Selecting "${input.text}" from dropdown at index: ${input.index}`;
    case 'browser_drag_drop':
      return input.elementSource && input.elementTarget
        ? `Dragging from "${input.elementSource}" to "${input.elementTarget}"`
        : `Dragging from (${input.coordSourceX}, ${input.coordSourceY}) to (${input.coordTargetX}, ${input.coordTargetY})`;
    case 'browser_click_coordinates':
      return `Clicking at coordinates: (${input.x}, ${input.y})`;
    default:
      return `Browser action: ${toolName.replace('browser_', '').replace(/_/g, ' ')}`;
  }
};

/**
 * Dataset tool text configurations.
 */
export const getDatasetToolText = (toolName: string, input: Record<string, unknown>): string => {
  switch (toolName) {
    case 'dataset_create':
      return `Creating dataset: ${input.name || 'New dataset'}`;
    case 'dataset_get':
      return `Getting dataset: ${input.id || 'Dataset'}`;
    case 'dataset_update':
      return `Updating dataset: ${input.id || 'Dataset'}`;
    case 'dataset_delete':
      return `Deleting dataset: ${input.id || 'Dataset'}`;
    case 'dataset_query':
      return 'Querying dataset';
    case 'dataset_add_row':
      return 'Adding row to dataset';
    case 'dataset_update_row':
      return 'Updating dataset row';
    case 'dataset_delete_row':
      return 'Deleting dataset row';
    case 'dataset_add_field':
      return 'Adding dataset field';
    case 'dataset_update_field':
      return 'Updating dataset field';
    case 'dataset_delete_field':
      return 'Deleting dataset field';
    case 'dataset_bulk_upload':
      return 'Bulk uploading to dataset';
    case 'dataset_list':
      return 'Listing datasets';
    default:
      return `Dataset operation: ${toolName.replace('dataset_', '').replace(/_/g, ' ')}`;
  }
};

/**
 * Computer control tool text configurations.
 */
export const getComputerToolText = (toolName: string, input: Record<string, unknown>): string => {
  switch (toolName) {
    case 'computer_click':
      return `Clicking at (${input.x}, ${input.y})`;
    case 'computer_drag_to':
      return `Dragging to (${input.x}, ${input.y})`;
    case 'computer_hotkey':
      return `Hotkey: ${input.keys}`;
    case 'computer_mouse_down':
      return 'Mouse down';
    case 'computer_mouse_up':
      return 'Mouse up';
    case 'computer_move_to':
      return `Moving to (${input.x}, ${input.y})`;
    case 'computer_press':
      return `Pressing: ${input.key}`;
    case 'computer_screenshot':
      return 'Taking screenshot';
    case 'computer_scroll':
      return `Scrolling ${input.direction}`;
    case 'computer_typing':
      return `Typing: ${input.text}`;
    case 'computer_wait':
      return `Waiting ${input.duration ?? 0.5}s`;
    default:
      return `Computer: ${toolName.replace('computer_', '').replace(/_/g, ' ')}`;
  }
};

/**
 * Tools that require special rendering (not using the generic pattern).
 */

/**
 * Check if a tool name matches a prefix pattern.
 */
